import { ApprovalsService, CancelError } from 'api-clients/monolith';
import { useCallback, useMemo, useState } from 'react';

import { useApiService } from 'hooks/useApiService';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';

import { SubjectType } from '../types';
import { Decision } from './DecisionFooter';
import { toApprovalRequest, toApprovalRequestList } from './normalize';
import { ApprovalRequest, ApprovalRequestList, RequestStatus } from './types';

/**
 * The generated client's payload, mapped to the types this feature reads
 * (AXHE-3947).
 *
 * Normalized on the way OUT of the hook rather than inside the service callback.
 * A `.then()` on a `CancelablePromise` returns a plain Promise, so mapping there
 * would silently strip `cancel()` — and `useApiService` calls it on unmount and
 * on refetch, which would throw instead of aborting the request.
 */
interface NormalizedQuery<T> {
  /** The payload once it has arrived, and the previous one while reloading. */
  data: T | null;
  isLoading: boolean;
  isError: boolean;
  /** Resolves once `data` answers this request or a later one, or it failed. */
  refetch: () => Promise<void>;
}

interface ApprovalRequestsQuery extends NormalizedQuery<ApprovalRequestList> {
  /**
   * `data` answers the filters and page on screen, possibly out of date by a
   * refetch in flight. False on the first load, after a failed one, and through
   * a filter change or a page turn, when `data` answers the previous question.
   */
  answersFilters: boolean;
}

/**
 * Whether the request has stopped moving. Not derivable from a level: a
 * rejection leaves later levels `pending`, and a cancellation leaves an open one
 * `active`, so the levels alone read as still-running.
 *
 * Beside the hook that returns the request rather than in the panel that first
 * needed it: the Send Offer drawer's read-only rail asks the same question of
 * the same payload (AXHE-4991), and the answer must not fork — its other reader
 * is `ApprovalLevelChain`, whose whole `levelState` derivation rests on it.
 * Importing it from `OfferReviewPanel` would also pull the decision panel into
 * that drawer's chunk to get one comparison.
 */
export const isSettled = (request: ApprovalRequest): boolean =>
  request.status !== 'pending';

/**
 * "No filter" as a first-class value rather than `undefined`.
 *
 * Each of the queue's dropdowns has an "All" entry that is a real, selectable
 * choice — it is what the control falls back to and what Clear restores — so
 * modelling it as the absence of a value leaves the menus unable to express
 * their own default. Converted to an omitted query parameter at the boundary
 * below, so the client's sentinel never reaches the wire.
 */
export const ALL = 'all';

export type TypeFilter = typeof ALL | SubjectType;
export type StatusFilter = typeof ALL | RequestStatus;

export interface QueueFilters {
  /** The server's own "assigned to or submitted by me" narrowing. */
  mine: boolean;
  type: TypeFilter;
  status: StatusFilter;
  /** Free text over the opening title and, on an offer, the applicant name. */
  search: string;
  page: number;
}

/**
 * One page of the approvals queue, filtered (AXHE-3934).
 *
 * These were hardcoded to `('pending', 'OfferLetter', true)` while this tab
 * existed only to make AXHE-3947's review panel reachable. That single call was
 * the whole of "no filters, offers only, page one" — parameterising it is what
 * turns the minimum list into the queue.
 *
 * The arguments are positional. Read their order from the generated client
 * rather than from the interface above — the two do not match, and nothing makes
 * them: `search` sits between `mine` and `page` on the wire.
 *
 * Every filter stays server-side, and two of them could not move even if it were
 * convenient. `mine` is a permission boundary rather than a view — the server
 * narrows a non-admin's list whatever they ask for, so the page cannot filter
 * what it was never sent. `status` and `type` are server-side for the same
 * reason paging is: a client-side predicate over one page filters the page, not
 * the queue.
 *
 * There is deliberately still no way to ask for somebody else's queue — `mine`
 * off widens to the account, which the server gates on its own, and carries no
 * user parameter.
 */
export const useApprovalRequests = (
  filters: QueueFilters,
): ApprovalRequestsQuery => {
  const { mine, type, status, search, page } = filters;

  // Memoized on the filters and the page, so its identity names the question a
  // payload answers: a filter change or a page turn builds a new one, and a
  // refetch re-sends the one on screen.
  const request = useCallback(
    () =>
      ApprovalsService.getInternalApiApprovalsRequests(
        status === ALL ? undefined : status,
        type === ALL ? undefined : type,
        mine,
        // Omitted rather than sent empty: the server treats a blank `search`
        // as no search anyway, and an empty parameter on every unsearched
        // request makes the query string say something it does not mean.
        search === '' ? undefined : search,
        page,
      ),
    [status, type, mine, search, page],
  );

  // The last payload to arrive, with the request that fetched it. Held here
  // rather than read from `useApiService`: its result does not say which
  // request a payload answers, and it drops the payload when one reload
  // interrupts another, which a second decision does to the first one's refetch.
  // Held across a page turn too, so the pager, which reads the payload, stays
  // mounted under the cursor.
  const [answer, setAnswer] = useState<{
    request: typeof request;
    payload: Awaited<ReturnType<typeof request>>;
  } | null>(null);

  const { result, refetch } = useApiService(
    useCallback(() => {
      const pending = request();
      // On a side branch, so the promise `useApiService` holds keeps its
      // `cancel()`. A failed load drops the answer: a retry then starts from the
      // spinner rather than from rows that predate the failure. A cancelled one
      // was superseded, and says nothing.
      pending.then(
        payload => setAnswer({ request, payload }),
        (error: unknown) => {
          if (!(error instanceof CancelError)) setAnswer(null);
        },
      );
      return pending;
    }, [request]),
  );

  const data = useMemo(
    () => (answer === null ? null : toApprovalRequestList(answer.payload)),
    [answer],
  );

  return {
    data,
    isLoading: result.isLoading,
    answersFilters: answer?.request === request,
    isError: result.isError,
    refetch,
  };
};

/**
 * One request's level tree, for the review panel.
 *
 * `enabled` guards the null id, but nothing passes one today: `OfferReviewPanel`
 * takes a required `requestId` and the queue mounts it conditionally, so the hook
 * only ever exists while a row is open. The guard is the backstop for a caller
 * that keeps the panel mounted instead — note that `useApiService` leaves
 * `result` at its loading initializer when disabled rather than reaching an idle
 * state, so such a caller would read `isLoading` as true while no row is open.
 *
 * The generated method is `…Requests1` — the codegen disambiguates index from
 * show by suffix, since both live at `/requests`. Named here so the suffix
 * appears in exactly one place.
 */
export const useApprovalRequest = (
  id: string | null,
): NormalizedQuery<ApprovalRequest> => {
  const { result, refetch } = useApiService(
    useCallback(
      () => ApprovalsService.getInternalApiApprovalsRequests1(id ?? ''),
      [id],
    ),
    { enabled: id !== null },
  );

  // Whenever the result carries a payload FOR THIS ID, which is two conditions
  // and both are load-bearing.
  //
  // `reloading` holds the previous response, and gated on `ready` alone this
  // goes null for the length of every refetch the panel itself starts — blanking
  // the letter, the rail and the footer to a spinner while somebody is mid-task
  // in them. Abandoning an edited draft is such a refetch (AXHE-4759).
  //
  // But `id` MOVES: a resubmission points the panel at the request that replaced
  // the one it was reading. Held through that, the previous payload's
  // `actionableStepIds` reach a footer whose decision mutation is already bound
  // to the new id — offering an Approve that posts a step belonging to the
  // superseded request. The server refuses it (`DecisionsController#fetch_step`
  // scopes the step to its request, so it 404s rather than recording anything),
  // which makes this a control that fails when pressed rather than a corruption
  // — and a control that fails when pressed is what this file exists to avoid.
  // Comparing the payload's own external_id keeps the reload behavior for a
  // refetch of the SAME request and drops it for a switch to another.
  const carried = 'data' in result ? result.data.request : null;
  const raw = carried?.external_id === id ? carried : null;
  const data = useMemo(
    () => (raw === null || raw === undefined ? null : toApprovalRequest(raw)),
    [raw],
  );

  return {
    data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch,
  };
};

export interface DecisionErrorBody {
  message?: string;
}

/**
 * Records one approve or reject.
 *
 * The response carries the request as the decision left it: settled, moved on to
 * its next level, or still waiting on the viewer's other steps.
 */
export const useRecordDecision = (
  id: string,
  options: {
    onSuccess: (request: ApprovalRequest) => void;
    onError: (body: DecisionErrorBody | undefined) => void;
  },
) => {
  const run = useCallback(
    (stepId: number, decision: Decision, comment?: string) =>
      ApprovalsService.postInternalApiApprovalsRequestsDecisions(id, {
        step_id: stepId,
        decision,
        comment,
      }),
    [id],
  );

  return useApiServiceMutation<
    Awaited<ReturnType<typeof run>>,
    typeof run,
    DecisionErrorBody
  >(run, {
    onSuccess: payload => {
      // `{ decision, request }` — the request as the decision left it.
      if (payload.request) {
        options.onSuccess(toApprovalRequest(payload.request));
        return;
      }

      // A 201 whose body carries no `request` is a contract break, and treating
      // it as nothing is the worst reading: the spinner stops, no message
      // appears, and the approver presses again into a 409. Routed to onError so
      // the surface says the decision could not be confirmed — which is true,
      // whatever the server actually recorded.
      options.onError(undefined);
    },
    onError: options.onError,
  });
};
